import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import React from 'react';
import { v4 as uuid } from 'uuid';
import { IImpersonationContextComponentProps } from '../../../../../models';
import { AutomationEditorStep } from '../../../../../models/Automations';
import { getDisplayName } from '../../../../../models/NameUtils';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useUserQuery } from '../../../../../queries';
import { IConnectionTypeComponentProps } from '../../../../containers/templates/EditCampaign/context';
import { Checkbox } from '../../../Checkbox';
import { DeprecatedSelect, ISelectOption } from '../../../DeprecatedSelect';
import {
	SelectAnEmployeeExternalEmailBody,
	SelectAnEmployeeInternalEmailBody,
	SelectAnEmployeeModal,
} from '../../../SelectAnEmployee';
import { styleSheet } from './../styles';

// The step prop can be either an AutomationEditorStep (from the step card) or a raw automation step
type StepProp =
	| AutomationEditorStep
	| Api.IEmailAutomationStep
	| Api.ITextingAutomationStep
	| Api.IHtmlNewsletterAutomationStep;

interface Props extends IImpersonationContextComponentProps, IConnectionTypeComponentProps {
	canEdit?: boolean;
	step: StepProp;
	sendFromLabel?: string;
	showInternalSendFromOption?: boolean;
	isBusy?: boolean;
	onSave?: (option: Api.ISendFromOptions, sendToSender: boolean) => void;
}

// Helper to check if step is an AutomationEditorStep
function isAutomationEditorStep(step: StepProp): step is AutomationEditorStep {
	return step && 'automationStep' in step && 'uuid' in step;
}

// Helper to get the automation step model from either type
function getAutomationStep(step: StepProp): Api.IAutomationStep | null {
	if (!step) {
		return null;
	}
	if (isAutomationEditorStep(step)) {
		return step.automationStep?.model || step.automationStep || null;
	}
	// It's already a raw step
	return step;
}

// Helper to get the step ID for use in element IDs
function getStepId(step: StepProp): string | undefined {
	if (isAutomationEditorStep(step)) {
		return step.automationStep?.id || step.uuid;
	}
	return step?.id;
}

export function StepSendFromOption(props: Props) {
	const userSession = useUserSession();
	const canEdit = props.canEdit !== false;
	const automationStep = getAutomationStep(props.step);
	const hasSendOptionSelected = () => {
		return !!automationStep?.sendFromOptions && automationStep.sendFromOptions.mode !== Api.SendEmailFrom.CurrentUser;
	};
	const [selectAnEmployeeModalIsOpen, setSelectAnEmployeeModalIsOpen] = React.useState(false);
	const [sendFrom, setSendFrom] = React.useState(automationStep?.sendFromOptions);
	const [sendToSender, setSendToSender] = React.useState(
		automationStep?._type === 'EmailAutomationStep'
			? !!(automationStep as Api.IEmailAutomationStep).options?.sendToSender
			: false
	);
	const [showingSendFrom, setShowingSendFrom] = React.useState(() => {
		return hasSendOptionSelected() && !sendToSender;
	});

	const [selectedSendFromUser, setSelectedSendFromUser] = React.useState<Api.IUser | null>();
	const onSaveSelectAnEmployee = (user: Api.IUser) => {
		if (!canEdit) {
			return;
		}
		setSelectAnEmployeeModalIsOpen(false);
		setSelectedSendFromUser(user);
		const updatedOption = { mode: Api.SendEmailFrom.SelectedUser, selectedUser: user.id };
		setSendFrom(updatedOption);
		props.onSave(updatedOption, sendToSender);
	};
	const connectionOptions = props.connectionTypes?.length
		? props.connectionTypes.map(connectionType => {
				return {
					id: `connectionType-${connectionType.value}`,
					dataContext: {
						mode: Api.SendEmailFrom.ConnectionType,
						connectionType: connectionType.value,
					} as Api.ISendFromOptions,
					text: connectionType.label,
				};
			})
		: [];
	const sendFromOptions = [
		{
			id: Api.SendEmailFrom.ContactOwner,
			dataContext: { mode: Api.SendEmailFrom.ContactOwner },
			text: 'Contact Owner',
		},
		...connectionOptions,
		{
			id: Api.SendEmailFrom.SelectedUser,
			dataContext: { mode: Api.SendEmailFrom.SelectedUser },
			text: 'Selected User',
		},
	];
	const onSendFromSelectionChanged = (option: ISelectOption<Api.ISendFromOptions>, wasSelected: boolean) => {
		if (!canEdit) {
			return;
		}
		if (wasSelected) {
			if (option.dataContext.mode === Api.SendEmailFrom.SelectedUser) {
				setSelectAnEmployeeModalIsOpen(true);
				return;
			}
			setSelectedSendFromUser(null);
			setSendFrom(option.dataContext);
			props.onSave(option.dataContext, sendToSender);
		}
	};
	const onCloseSelectAnEmployeeModal = () => setSelectAnEmployeeModalIsOpen(false);
	const selectedUserQuery = useUserQuery({
		enabled: Boolean(automationStep?.sendFromOptions?.selectedUser),
		id: automationStep?.sendFromOptions?.selectedUser ?? '',
		impersonationContext: props.impersonationContext,
	});

	// Sync state when automationStep data changes (e.g., after refresh)
	React.useEffect(() => {
		if (!automationStep) {
			return;
		}

		const stepSendFromOptions = automationStep.sendFromOptions;
		const stepSendToSender =
			automationStep._type === 'EmailAutomationStep'
				? !!(automationStep as Api.IEmailAutomationStep).options?.sendToSender
				: false;

		setSendFrom(stepSendFromOptions);
		setSendToSender(stepSendToSender);

		const hasOption = !!stepSendFromOptions && stepSendFromOptions.mode !== Api.SendEmailFrom.CurrentUser;
		setShowingSendFrom(hasOption && !stepSendToSender);
	}, [
		automationStep?.sendFromOptions,
		automationStep?._type,
		(automationStep as Api.IEmailAutomationStep)?.options?.sendToSender,
	]);
	const onShowSendFromCheckboxChanged = () => {
		if (!canEdit) {
			return;
		}
		if (showingSendFrom) {
			setSendFrom(null);
			setShowingSendFrom(false);
			setSelectedSendFromUser(null);
			props.onSave(null, sendToSender);
		} else {
			const newSendFrom = sendFromOptions[0].dataContext;
			const newSendToSender = false;
			setSendFrom(newSendFrom);
			setShowingSendFrom(true);
			setSendToSender(newSendToSender);
			setSelectedSendFromUser(null);
			props.onSave(newSendFrom, newSendToSender);
		}
	};
	const getSendFromOptionLabel = (option: Api.ISendFromOptions) => {
		const allOptions = sendFromOptions;
		if (!allOptions) {
			return null;
		}

		if (option.mode === Api.SendEmailFrom.SelectedUser) {
			if (selectedSendFromUser) {
				return getDisplayName(selectedSendFromUser);
			}

			return selectedUserQuery.data ? getDisplayName(selectedUserQuery.data) : 'Selected User';
		}
		return allOptions.find(
			i => i.dataContext.mode === option.mode && i.dataContext.connectionType === option.connectionType
		)?.text;
	};
	const onSendToSenderCheckboxChanged = () => {
		if (!canEdit) {
			return;
		}
		if (sendToSender) {
			const newSendToSender = false;
			setSendFrom(null);
			setSendToSender(newSendToSender);
			setSelectedSendFromUser(null);
			props.onSave(null, newSendToSender);
		} else {
			const newSendFrom = sendFromOptions[0].dataContext;
			const newSendToSender = true;
			setSendFrom(newSendFrom);
			setSendToSender(newSendToSender);
			setShowingSendFrom(false);
			setSelectedSendFromUser(null);
			props.onSave(newSendFrom, newSendToSender);
		}
	};

	const stepId = getStepId(props.step);

	return (
		<>
			<Checkbox
				className={css(styleSheet.addTagOnLinkClickCheckbox)}
				id={stepId ? `automation-send-from-checkbox-${stepId}` : uuid()}
				checked={showingSendFrom}
				disabled={(props.isBusy ?? false) || !canEdit}
				type='large'
				onChange={onShowSendFromCheckboxChanged}
			>
				<div>
					<p className={css(styleSheet.checkboxText)}>{props?.sendFromLabel ?? 'Always send this step from'}</p>
				</div>
			</Checkbox>
			{showingSendFrom ? (
				<DeprecatedSelect
					maxOptionsToShow={99}
					onOptionClick={onSendFromSelectionChanged}
					options={sendFromOptions}
					selectedOptionTitle={getSendFromOptionLabel(sendFrom)}
				/>
			) : null}
			{props?.showInternalSendFromOption && userSession.account.features.automation.showInternalSendOption ? (
				<>
					<Checkbox
						className={css(styleSheet.addTagOnLinkClickCheckbox)}
						id={stepId ? `automation-send-to-checkbox-${stepId}` : uuid()}
						checked={sendToSender}
						disabled={(props.isBusy ?? false) || !canEdit}
						type='large'
						onChange={onSendToSenderCheckboxChanged}
					>
						<div>
							<p className={css(styleSheet.checkboxText)}>Send this email to an internal user</p>
						</div>
					</Checkbox>
					{sendToSender ? (
						<DeprecatedSelect
							maxOptionsToShow={99}
							onOptionClick={onSendFromSelectionChanged}
							options={sendFromOptions}
							selectedOptionTitle={getSendFromOptionLabel(sendFrom)}
						/>
					) : null}
				</>
			) : null}
			{selectAnEmployeeModalIsOpen ? (
				<SelectAnEmployeeModal
					bodyText={sendToSender ? SelectAnEmployeeInternalEmailBody : SelectAnEmployeeExternalEmailBody}
					impersonationContext={props.impersonationContext}
					onClose={onCloseSelectAnEmployeeModal}
					onUserSelected={onSaveSelectAnEmployee}
				/>
			) : null}
		</>
	);
}
